import { Zap, Clock, MoonStar, Sparkles } from "lucide-react";

/**
 * ResponsivenessBadge — shows the athlete's reply-rate / response-time band.
 *
 * Props:
 *   r: { samples, response_rate_pct, avg_response_hours, last_active_days, band }
 *   size: "sm" (card) | "md" (default) | "lg" (detail page)
 *   inline: true => single-line compact; false => stacked block
 */
export default function ResponsivenessBadge({ r, size = "md", inline = false, testid }) {
  if (!r) return null;
  const cfg = {
    fast:   { Icon: Zap,       label: "Fast replier",  border: "border-volt",          text: "text-volt",          hint: "Replies fast · high reply rate" },
    active: { Icon: Sparkles,  label: "Active",        border: "border-volt/50",       text: "text-volt/90",       hint: "Replies regularly" },
    slow:   { Icon: MoonStar,  label: "Slower",        border: "border-line",          text: "text-ink-muted",     hint: "Lower historical reply rate" },
    new:    { Icon: Clock,     label: "New",           border: "border-line",          text: "text-ink-muted",     hint: "Not enough partnership data yet" },
  }[r.band] || null;
  if (!cfg) return null;
  const { Icon } = cfg;
  const padX = size === "sm" ? "px-1.5" : size === "lg" ? "px-3" : "px-2";
  const padY = size === "sm" ? "py-0.5" : size === "lg" ? "py-1.5" : "py-1";
  const txt = size === "sm" ? "text-[9px]" : "text-[10px]";

  if (inline) {
    return (
      <span data-testid={testid} title={cfg.hint}
        className={`inline-flex items-center gap-1 border ${cfg.border} ${cfg.text} ${padX} ${padY} ${txt} uppercase tracking-widest`}>
        <Icon className={size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} /> {cfg.label}
      </span>
    );
  }

  // Stacked detail variant for AthleteDetail page
  return (
    <div className={`border ${cfg.border} p-4 surface`} data-testid={testid}>
      <div className={`flex items-center gap-2 ${cfg.text} uppercase tracking-widest text-xs`}>
        <Icon className="w-4 h-4" /> {cfg.label}
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <Stat label="Reply rate" v={r.response_rate_pct != null ? `${r.response_rate_pct}%` : "—"} accent={r.band === "fast" || r.band === "active"} />
        <Stat label="Avg reply" v={r.avg_response_hours == null ? "—" : r.avg_response_hours < 1 ? "<1h" : `${r.avg_response_hours}h`} />
        <Stat label="Last active" v={r.last_active_days != null ? (r.last_active_days === 0 ? "today" : `${r.last_active_days}d ago`) : "—"} />
      </div>
      <div className="text-[10px] text-ink-muted mt-3">
        Based on {r.samples || 0} brand conversation{r.samples === 1 ? "" : "s"}. {cfg.hint}.
      </div>
    </div>
  );
}

function Stat({ label, v, accent }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-ink-muted">{label}</div>
      <div className={`font-mono text-sm mt-1 ${accent ? "text-volt" : "text-white"}`}>{v}</div>
    </div>
  );
}
